import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);

  const fetchFavoriteIds = useCallback(async () => {
    if (!isAuthenticated) {
      setFavoriteIds([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/favorites/ids`);
      setFavoriteIds(response.data);
    } catch (error) {
      // Non-critical: favorites fail gracefully without blocking UI
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchFavoriteIds();
  }, [fetchFavoriteIds]);

  const isFavorite = (productId) => favoriteIds.includes(productId);

  const toggleFavorite = async (productId) => {
    if (isFavorite(productId)) {
      // Remove
      setFavoriteIds(prev => prev.filter(id => id !== productId));
      try {
        await axios.delete(`${API_URL}/api/favorites/${productId}`);
        toast.success('Retiré des favoris');
      } catch (error) {
        setFavoriteIds(prev => [...prev, productId]);
        toast.error('Erreur');
      }
    } else {
      // Add
      setFavoriteIds(prev => [...prev, productId]);
      try {
        await axios.post(`${API_URL}/api/favorites/${productId}`, {});
        toast.success('Ajouté aux favoris');
      } catch (error) {
        setFavoriteIds(prev => prev.filter(id => id !== productId));
        toast.error('Erreur');
      }
    }
  };

  const value = useMemo(() => ({ favoriteIds, isFavorite, toggleFavorite, fetchFavoriteIds }), [favoriteIds, isFavorite, toggleFavorite, fetchFavoriteIds]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext;
